import React, { useState, useRef } from 'react';
import { PitchingMonitor as NyyPitchingMonitor, LoadingSpinner, defaultPitchingMonitorFilter } from 'yankees-react-utils';
import { useTranslation } from 'react-i18next';
import { PitchingBreakdown } from '../../../../data/data';
import TopLevelFilter from './TopLevelFilter';
import { useMultiVideoFn, useOktaQuery, usePitchVideoFn } from '../../../../data';
import { VideoPlayerModal } from '../../../../components/VideoPlayer';
import StatTooltip from '../../../../components/StatTooltip';
import usePlayerSelector from '../../../../utils/usePlayerSelector';

let abortController = null;

const PitchingMonitor = () => {
    const { t: translate } = useTranslation();
    const { PlayerID, Throws, Team, IsCatcher } = usePlayerSelector();
    const innerPitchVideoFn = usePitchVideoFn();
    const innerMultiVideoFn = useMultiVideoFn();
    const modalContainer = useRef(null);

    const [levels, setLevels] = useState([]);
    const [org, setOrg] = useState({ name: 'All', value: null });
    const [year, setYear] = useState(null);
    const [startDate, setStartDate] = useState(null);
    const [maxPAs, setMaxPAs] = useState(0);
    const [endDate, setEndDate] = useState(null);
    const [pitcher, setPitcher] = useState(null);
    const [liveFilter, setLiveFilter] = useState(defaultPitchingMonitorFilter);
    const [videoPlaylist, setVideoPlaylist] = useState(null);
    const [showVideoPlayerModal, setShowVideoPlayerModal] = useState(false);

    const pitcherId = IsCatcher ? pitcher?.PlayerID : PlayerID;

    const { data: pitchmon, isFetching: isFetchingPitchmon } = useOktaQuery(
        ['pitchmon', pitcherId, {
            yearid: startDate !== null || endDate !== null ? null : year?.value,
            levelids: levels?.filter((l) => l.checked).map((l) => l.value).join(','),
            orgid: org?.value,
            startdate: startDate,
            enddate: endDate,
        }],
        (t) => {
            if (abortController) {
                abortController.abort();
            }
            const levelIds = levels?.filter((l) => l.checked).map((l) => l.value).filter((x) => !!x);
            abortController = new AbortController();
            return PitchingBreakdown.pitchmonPitches(t, pitcherId, {
                yearid: startDate !== null || endDate !== null ? null : year?.value,
                levelids: levelIds.length ? levelIds : undefined,
                orgid: org?.value,
                startdate: startDate,
                enddate: endDate,
            }, { signal: abortController.signal });
        },
        {
            enabled: !!levels.length && (!!year?.value || !!startDate || !!endDate) && !!pitcherId,
            onSuccess: (data) => {
                const lastxpas = data?.pitches?.reduce((max, p) => Math.max(p.lastxpa, max), 0) || 100;
                setMaxPAs(lastxpas);
            },
        },
    );

    const pitchVideoFn = async (gameid, eventseq, pitchseq) => {
        setShowVideoPlayerModal(true);
        setVideoPlaylist(await innerPitchVideoFn(gameid, eventseq, pitchseq));
    };
    const multiVideoFn = async (pitches) => {
        setShowVideoPlayerModal(true);
        setVideoPlaylist(await innerMultiVideoFn(pitches));
    };

    const isLoading = isFetchingPitchmon || !pitchmon;

    return (
        <div ref={modalContainer}>
            <div className="d-flex justify-content-between align-items-center">
                <h3>Pitching Monitor</h3>
                <TopLevelFilter
                    playerId={PlayerID}
                    defaultLevelId={Team?.Level?.LevelID}
                    levels={levels}
                    setLevels={setLevels}
                    org={org}
                    setOrg={setOrg}
                    year={year}
                    setYear={setYear}
                    startDate={startDate}
                    setStartDate={setStartDate}
                    endDate={endDate}
                    setEndDate={setEndDate}
                    pitcher={pitcher}
                    setPitcher={setPitcher}
                />
            </div>
            {pitcherId
                ? (isLoading ? <div className="py-5 text-center"><LoadingSpinner /></div> : (
                    <div className="border rounded mt-3 p-3 bg-white">
                        <NyyPitchingMonitor
                            liveFilter={liveFilter}
                            setLiveFilter={setLiveFilter}
                            pitches={pitchmon?.pitches ?? []}
                            pitchVideoFn={pitchVideoFn}
                            multiVideoFn={multiVideoFn}
                            levels={levels?.filter((l) => l.checked).map((l) => l.value) ?? []}
                            statTooltip={StatTooltip}
                            maxPAs={maxPAs}
                            isLhp={Throws === 2}
                            hideBatTrackingStats
                            enablePlayAllInStatsTable
                        />
                    </div>
                )) : <div className="text-center py-4 px-3 mt-3">{translate('sections.reports.pitcher.use-filters')}</div>}
            <VideoPlayerModal
                playlist={videoPlaylist}
                show={showVideoPlayerModal}
                onHide={() => { setShowVideoPlayerModal(false); setVideoPlaylist(null); }}
                container={modalContainer}
            />
        </div>
    );
};

export default PitchingMonitor;
